<template>
  <div class="location-search">
    <div class="location-search__form">
      <div class="location-search__set-center-input">
        <ui-model-provider v-slot="{ value, updateValue }" :value.sync="searchTerm">
          <ui-input
            :placeholder="$t('wizard_step.centering.set_center_input_placeholder')"
            :value="value"
            required
            @input="updateValue"
          />
        </ui-model-provider>
      </div>
    </div>
    <div ref="attribution" />
    <div class="location-search__results">
      <p v-if="hasNoResults" class="location-search__no-results-warning u-reset">
        {{ $t('wizard_step.centering.set_center_no_results') }}
      </p>
      <ul v-if="locationResults.length" class="location-search__result-items u-reset">
        <li
          v-for="(locationResult, locationResultIdx) in locationResults"
          :key="locationResultIdx"
          class="location-search__result-item"
        >
          <button class="location-search__result" @click="onRequestDetails(locationResult.placeId)">
            {{ locationResult.description }}
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
  import { debounce } from 'lodash-es'
  import UiModelProvider from '@/components/form/ui-model-provider/ui-model-provider'
  import UiInput from '@/components/form/ui-input/ui-input'
  import {
    onAutocompleteRequest,
    onGetDetailsRequest,
    TARGET_TYPES,
  } from '@/components/location-search/util'

  export default {
    components: { UiInput, UiModelProvider },
    props: {
      rotation: {
        type: Array,
        required: true,
      },
    },
    data() {
      return {
        isApiLoaded: false,
        isApiInitialized: false,
        isSearching: false,
        searchTerm: '',
        searchResults: [],
        GoogleMaps: null,
        AutocompleteService: null,
        PlacesService: null,
      }
    },
    computed: {
      hasNoResults() {
        return !this.isSearching && !this.locationResults.length && this.searchTerm
      },
      locationResults() {
        return this.searchResults
          .filter(({ types }) => types.some((type) => TARGET_TYPES.includes(type)))
          .map((searchResult) => {
            const { place_id: placeId, description } = searchResult
            return { placeId, description }
          })
      },
    },
    watch: {
      searchTerm() {
        this.isSearching = true
        this.debouncedSearchGeoLocation()
      },
      isApiLoaded() {
        this.PlacesService = new this.GoogleMaps.places.PlacesService(this.$refs.attribution)
        this.isApiInitialized = true
      },
    },
    async created() {
      const { Api } = await new Promise((resolve) => {
        require.ensure(
          [],
          () => {
            return Promise.all([
              require('./google-maps').default(this.$config.googleMapsApiKey),
            ]).then(([Api]) => resolve({ Api }))
          },
          null,
          'google-maps',
        )
      })

      this.GoogleMaps = Api
      this.AutocompleteService = new this.GoogleMaps.places.AutocompleteService()
      this.isApiLoaded = true
    },
    methods: {
      onRequestDetails(placeId) {
        this.getLocationDetails(placeId)
      },
      clearSearch() {
        this.searchTerm = ''
        this.searchResults = []
        this.isSearching = false
      },
      searchGeoLocation() {
        const { GoogleMaps } = this

        if (!this.searchTerm) {
          this.clearSearch()
          return
        }

        this.AutocompleteService.getPlacePredictions(
          { input: this.searchTerm },
          (results, status) => {
            this.isSearching = false
            this.searchResults = onAutocompleteRequest({ GoogleMaps }, results, status)
          },
        )
      },
      debouncedSearchGeoLocation: debounce(function () {
        this.searchGeoLocation()
      }, 400),
      getLocationDetails(placeId) {
        const { GoogleMaps } = this

        if (!placeId) {
          return
        }

        this.PlacesService.getDetails(
          { fields: ['geometry.location'], placeId },
          (result, status) => {
            const { lat, lng } = onGetDetailsRequest({ GoogleMaps }, result, status)
            this.$emit('update', [lng * -1, lat * -1, 0])
            this.clearSearch()
          },
        )
      },
    },
  }
</script>

<style lang="scss" src="./_location-search.scss" />
